import { Auth } from "aws-amplify"
import Logger from "@common/Logger"
import {
  Message,
  Follow,
  WebSocketIncomingMessage,
  WebSocketOutgoingMessage,
  WebSocketPingMessage,
  WebSocketTypingMessage,
  WebSocketUserStatusMessage,
  ZeefWebSocketConfig,
  ZeefEventObserver as ZeefEventObserverInterface,
} from "./ZeefTypes"

// Base URL for WebSocket connections
const ZEEF_WS_URL =
  process.env.REACT_APP_ZEEF_WS_URL ||
  process.env.REACT_APP_ZEEF_API_URL?.replace(/^https/, "wss").replace(/^http/, "ws") ||
  ""

// ===== Abstract Observer Class =====

/**
 * Abstract base class for observing ZEEF WebSocket events.
 * Extend this class to receive real-time events from the ZEEF messaging system.
 */
export abstract class ZeefEventObserver {
  protected eventFilters: {
    messageTypes?: ("regular" | "broadcast")[]
    userIds?: string[]
  } = {}

  /**
   * Set filters for which events this observer should receive
   */
  setEventFilters(filters: { messageTypes?: ("regular" | "broadcast")[]; userIds?: string[] }): void {
    this.eventFilters = filters
  }

  /**
   * Check if this observer should receive the given event based on filters
   */
  public shouldReceiveEvent(event: WebSocketIncomingMessage): boolean {
    // Message type filter for message_received events
    if (
      event.type === "message_received" &&
      this.eventFilters.messageTypes &&
      !this.eventFilters.messageTypes.includes(event.data.type)
    ) {
      return false
    }

    // User ID filter for user events
    if (
      (event.type === "user_online" || event.type === "user_offline") &&
      this.eventFilters.userIds &&
      !this.eventFilters.userIds.includes(event.data.user_id)
    ) {
      return false
    }

    return true
  }

  /**
   * Called when any event is received (before specific event handlers)
   */
  onEvent?(event: WebSocketIncomingMessage): void

  /**
   * Called when a new message is received
   */
  onMessageReceived?(message: Message): void

  /**
   * Called when a new follow relationship is created
   */
  onFollowAdded?(follow: Follow): void

  /**
   * Called when a follow relationship is removed
   */
  onFollowRemoved?(follow: Follow): void

  /**
   * Called when a user comes online
   */
  onUserOnline?(data: { user_id: string; timestamp: string }): void

  /**
   * Called when a user goes offline
   */
  onUserOffline?(data: { user_id: string; timestamp: string }): void

  /**
   * Called when an error occurs
   */
  onError?(error: string): void

  /**
   * Called when WebSocket connection opens
   */
  onConnectionOpen?(): void

  /**
   * Called when WebSocket connection closes
   */
  onConnectionClose?(code: number, reason: string): void

  /**
   * Called when WebSocket connection fails
   */
  onConnectionError?(error: Event): void
}

// ===== WebSocket Client =====

/**
 * WebSocket client for real-time communication with ZEEF messaging system
 */
export class ZeefWebSocketClient {
  private ws: WebSocket | null = null
  private observers: Set<ZeefEventObserver> = new Set()
  private config: Required<ZeefWebSocketConfig>
  private reconnectAttempts = 0
  private reconnectTimer: NodeJS.Timeout | null = null
  private connectionPromise: Promise<void> | null = null
  private connectionResolver: (() => void) | null = null
  private connectionRejector: ((error: Error) => void) | null = null
  private pingInterval: NodeJS.Timeout | null = null

  constructor(config: ZeefWebSocketConfig = {}) {
    this.config = {
      baseUrl: ZEEF_WS_URL,
      reconnectInterval: 1000,
      maxReconnectAttempts: 10,
      pingInterval: 30000,
      ...config,
    }
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolver = resolve
      this.connectionRejector = reject
      this.doConnect()
    })

    return this.connectionPromise
  }

  private async doConnect(): Promise<void> {
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.connectionResolver?.()
        return
      }

      const session = await Auth.currentSession()
      const token = session.getIdToken().getJwtToken()
      const wsUrl = `${this.config.baseUrl}/api/ws?token=${encodeURIComponent(token)}`

      Logger.debug("Connecting to ZEEF WebSocket:", wsUrl.replace(/token=[^&]+/, "token=***"))

      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        Logger.debug("WebSocket connected to ZEEF")
        this.reconnectAttempts = 0
        this.connectionPromise = null
        this.connectionResolver?.()
        this.notifyObservers("onConnectionOpen")
        this.startPingInterval()
      }

      this.ws.onclose = (event) => {
        Logger.debug(`ZEEF WebSocket disconnected: ${event.code} - ${event.reason}`)
        this.connectionPromise = null
        this.stopPingInterval()
        this.notifyObservers("onConnectionClose", event.code, event.reason)

        if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = (error) => {
        Logger.error("ZEEF WebSocket error:", error)
        this.connectionRejector?.(new Error("WebSocket connection failed"))
        this.notifyObservers("onConnectionError", error)
        this.handleConnectionError()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketIncomingMessage
          this.handleEvent(data)
        } catch (error) {
          Logger.error("Failed to parse ZEEF WebSocket message:", error, event.data)
        }
      }
    } catch (error) {
      Logger.error("Failed to create ZEEF WebSocket connection:", error)
      this.connectionRejector?.(error as Error)
      this.handleConnectionError()
    }
  }

  private handleConnectionError(): void {
    this.connectionPromise = null
    this.stopPingInterval()
    if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectAttempts++
    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      30000 // Max 30 seconds
    )

    Logger.debug(
      `Scheduling ZEEF WebSocket reconnection attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`
    )

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.doConnect()
    }, delay)
  }

  private startPingInterval(): void {
    if (this.config.pingInterval > 0) {
      this.pingInterval = setInterval(() => {
        this.sendPing()
      }, this.config.pingInterval)
    }
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.stopPingInterval()

    if (this.ws) {
      this.ws.close(1000, "Client disconnecting")
      this.ws = null
    }

    this.connectionPromise = null
    this.reconnectAttempts = 0
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * Send a ping message to keep the connection alive
   */
  async sendPing(): Promise<void> {
    if (!this.isConnected()) {
      return
    }

    const payload: WebSocketPingMessage = {
      type: "ping",
      data: {},
    }

    if (this.ws) {
      this.ws.send(JSON.stringify(payload))
      Logger.debug("Sent ZEEF WebSocket ping")
    }
  }

  /**
   * Send a typing indicator
   */
  async sendTyping(conversationId: string): Promise<void> {
    if (!this.isConnected()) {
      await this.connect()
    }

    const payload: WebSocketTypingMessage = {
      type: "typing",
      data: {
        conversation_id: conversationId,
      },
    }

    if (this.ws) {
      this.ws.send(JSON.stringify(payload))
      Logger.debug("Sent ZEEF typing indicator:", conversationId)
    } else {
      throw new Error("WebSocket not connected")
    }
  }

  /**
   * Send a user status request
   */
  async sendUserStatus(): Promise<void> {
    if (!this.isConnected()) {
      await this.connect()
    }

    const payload: WebSocketUserStatusMessage = {
      type: "user_status",
      data: {},
    }

    if (this.ws) {
      this.ws.send(JSON.stringify(payload))
      Logger.debug("Sent ZEEF user status request")
    } else {
      throw new Error("WebSocket not connected")
    }
  }

  /**
   * Send a generic WebSocket message
   */
  async sendMessage(message: WebSocketOutgoingMessage): Promise<void> {
    if (!this.isConnected()) {
      await this.connect()
    }

    if (this.ws) {
      this.ws.send(JSON.stringify(message))
      Logger.debug("Sent ZEEF WebSocket message:", message.type)
    } else {
      throw new Error("WebSocket not connected")
    }
  }

  /**
   * Add an event observer
   */
  addObserver(observer: ZeefEventObserver): void {
    this.observers.add(observer)
  }

  /**
   * Remove an event observer
   */
  removeObserver(observer: ZeefEventObserver): void {
    this.observers.delete(observer)
  }

  /**
   * Remove all observers
   */
  clearObservers(): void {
    this.observers.clear()
  }

  private handleEvent(event: WebSocketIncomingMessage): void {
    Logger.debug("Received ZEEF WebSocket event:", event)

    for (const observer of this.observers) {
      if (!observer.shouldReceiveEvent(event)) {
        continue
      }

      try {
        // Call general event handler first
        observer.onEvent?.(event)

        // Call specific event handler
        switch (event.type) {
          case "pong":
            // Handle pong silently
            break
          case "message_received":
            observer.onMessageReceived?.(event.data)
            break
          case "follow_added":
            observer.onFollowAdded?.(event.data)
            break
          case "follow_removed":
            observer.onFollowRemoved?.(event.data)
            break
          case "user_online":
            observer.onUserOnline?.(event.data)
            break
          case "user_offline":
            observer.onUserOffline?.(event.data)
            break
          case "error":
            observer.onError?.(event.data.message)
            break
        }
      } catch (error) {
        Logger.error("Error in ZEEF WebSocket event observer:", error)
      }
    }
  }

  private notifyObservers(method: string, ...args: unknown[]): void {
    for (const observer of this.observers) {
      try {
        const observerMethod = (observer as unknown as Record<string, unknown>)[method]
        if (typeof observerMethod === "function") {
          (observerMethod as (...args: unknown[]) => void).apply(observer, args)
        }
      } catch (error) {
        Logger.error(`Error in ZEEF WebSocket observer ${method}:`, error)
      }
    }
  }
}

// ===== WebSocket Manager Singleton =====

/**
 * Singleton manager for global ZEEF WebSocket connection coordination
 */
export class ZeefWebSocketManager {
  private static instance: ZeefWebSocketManager | null = null
  private client: ZeefWebSocketClient | null = null

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): ZeefWebSocketManager {
    if (!ZeefWebSocketManager.instance) {
      ZeefWebSocketManager.instance = new ZeefWebSocketManager()
    }
    return ZeefWebSocketManager.instance
  }

  /**
   * Initialize the WebSocket client with configuration
   */
  initialize(config?: ZeefWebSocketConfig): ZeefWebSocketClient {
    if (this.client) {
      this.client.disconnect()
    }

    this.client = new ZeefWebSocketClient(config)
    return this.client
  }

  /**
   * Get the current WebSocket client (initialize if needed)
   */
  getClient(): ZeefWebSocketClient {
    if (!this.client) {
      this.client = new ZeefWebSocketClient()
    }
    return this.client
  }

  /**
   * Connect to WebSocket if not already connected
   */
  async connect(): Promise<void> {
    const client = this.getClient()
    if (!client.isConnected()) {
      await client.connect()
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.client) {
      this.client.disconnect()
    }
  }

  /**
   * Add a global observer
   */
  addObserver(observer: ZeefEventObserver): void {
    this.getClient().addObserver(observer)
  }

  /**
   * Remove a global observer
   */
  removeObserver(observer: ZeefEventObserver): void {
    if (this.client) {
      this.client.removeObserver(observer)
    }
  }

  /**
   * Send a ping message
   */
  async sendPing(): Promise<void> {
    const client = this.getClient()
    await client.sendPing()
  }

  /**
   * Send a typing indicator
   */
  async sendTyping(conversationId: string): Promise<void> {
    const client = this.getClient()
    await client.sendTyping(conversationId)
  }

  /**
   * Send a user status request
   */
  async sendUserStatus(): Promise<void> {
    const client = this.getClient()
    await client.sendUserStatus()
  }

  /**
   * Send a generic WebSocket message
   */
  async sendMessage(message: WebSocketOutgoingMessage): Promise<void> {
    const client = this.getClient()
    await client.sendMessage(message)
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.client?.isConnected() || false
  }
}
